/**
 * Notifications API — RTK Query endpoints injected into the shared baseApi.
 *
 * The three legacy calls (apiservice.js:900-949):
 *   GET    /notifications?user_email=&app_name=   -> { Data, Count }
 *   PATCH  /notifications/read?notification_id=
 *   DELETE /notifications/del?user_email=&app_name=   (clear ALL — no single delete)
 *
 * `app_name` IS sent as a query param, even though apiClient already puts the
 * resolved variant on every request as the `Appname` header. An earlier version
 * sent only the header, on the assumption that the two were interchangeable —
 * they are not. Legacy sends BOTH on exactly these two calls (apiservice.js:903
 * and :934, header `AppName` plus `&app_name=`), and the server contract is
 * whatever legacy exercises, not what looks redundant from here.
 *
 * These endpoints are LIVE — they ignore env.useMocks and always hit the real
 * API. The mock envelope is kept only as the contract fixture the unit tests
 * assert against, not as a runtime fallback.
 */
import { appNameFor } from '@services/apiClient';
import { API_ENDPOINTS } from '@services/apiEndpoints';
import { baseApi } from '@services/baseApi';

const { NOTIFICATIONS, NOTIFICATION_READ, NOTIFICATIONS_CLEAR_ALL } = API_ENDPOINTS.APP_BAR;

/**
 * Normalise the server envelope into the shape components want.
 *
 * Two legacy bugs are fixed here rather than reproduced:
 *  - on an empty result the legacy method falls back to `[]`, so `response.Count`
 *    is undefined and the initial load assigns `notifications = undefined`,
 *    which then throws on `.length`. Defaulting both fields removes that.
 *  - `Count` is trusted as the unread count (it is server-computed and covers
 *    more than the current page), but it is clamped to a number so a missing
 *    field can never render as `NaN` in the badge.
 */
export function normalizeNotifications(response) {
  const items = Array.isArray(response?.Data) ? response.Data : [];
  const count = Number(response?.Count);
  return { items, unreadCount: Number.isFinite(count) ? count : 0 };
}

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      query: (userEmail) => ({
        url: NOTIFICATIONS,
        params: { user_email: userEmail, app_name: appNameFor() },
      }),
      transformResponse: normalizeNotifications,
      providesTags: ['Notification'],
    }),

    markNotificationRead: builder.mutation({
      query: (notificationId) => ({
        url: NOTIFICATION_READ,
        method: 'PATCH',
        params: { notification_id: notificationId },
      }),
      invalidatesTags: ['Notification'],
    }),

    clearAllNotifications: builder.mutation({
      query: (userEmail) => ({
        url: NOTIFICATIONS_CLEAR_ALL,
        method: 'DELETE',
        params: { user_email: userEmail, app_name: appNameFor() },
      }),
      invalidatesTags: ['Notification'],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationReadMutation,
  useClearAllNotificationsMutation,
} = notificationsApi;
